import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDocumentTitle } from '@gokrew/ui';
import { OnboardingRoute } from '@gokrew/models/onboarding';
import { TranslationNS } from '@/constants/translation-namespaces';
import { useBreadcrumbs } from '@/hooks/useBreadcrumbs';

/**
 * Routes outside `MainLayout` have no breadcrumbs, so they are named by path
 * prefix instead. First match wins, so list a longer prefix before a shorter
 * one it starts with.
 */
const STANDALONE_TITLES: [prefix: string, key: string][] = [
  ['/login', 'sign_in'],
  ['/mock-login', 'sign_in'],
  ['/mock-session-expired', 'sign_in'],
  ['/signup-v2/workspace', 'workspace'],
  ['/signup', 'sign_up'],
  ['/forgot-password', 'forgot_password'],
  ['/reset-password', 'reset_password'],
  ['/verify-email', 'verify_email'],
  [OnboardingRoute.Base, 'onboarding'],
  ['/ai-assistant', 'ai_assistant'],
  ['/slack', 'slack'],
  ['/teams', 'teams'],
  ['/oauth/consent', 'mcp_consent'],
];

/** Crumbs that name a record type rather than a place, so add nothing. */
const SKIPPED_CRUMBS = new Set(['detail', 'organization']);

/**
 * Names the browser tab after the current route: the same sidebar label the
 * header shows (`Recruiting | KollabHR`), led by the tab or sub-page when
 * there is one (`Policies · Leave Management | KollabHR`). Mount once, inside
 * the router.
 */
export function useRouteDocumentTitle() {
  const { pathname } = useLocation();
  const breadcrumbs = useBreadcrumbs();
  const { t } = useTranslation(TranslationNS.sidebar);

  let title: string | null = null;

  if (pathname === '/dashboard') {
    title = t('nav.dashboard');
  } else if (breadcrumbs) {
    const page = t(`nav.${breadcrumbs.pageCrumb}`);
    const leaf = [...breadcrumbs.crumbs]
      .reverse()
      .find((crumb) => !SKIPPED_CRUMBS.has(crumb));
    title =
      leaf && leaf !== breadcrumbs.pageCrumb
        ? `${t(`nav.${leaf}`)} · ${page}`
        : page;
  } else {
    const match = STANDALONE_TITLES.find(([prefix]) =>
      pathname.startsWith(prefix)
    );
    if (match) title = t(`document_title.${match[1]}`);
    else if (pathname !== '/') title = t('document_title.not_found');
  }

  useDocumentTitle(title);
}
